import React, { Dispatch, useEffect, useState } from 'react';
import { Modal } from 'antd';
import {
  getAdminNewsList as getDownLoadNewsList,
  deleteNews as deleteDownLoadNews,
} from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../store/modules/common';
import { SystemMenuId } from '../../model/system/menu';
import './style.vditor.scss';
import getFormatMessage from '../../locales/get-format-message';
import { IDownloadNews } from '../../model/api-modules/download-center';
import {
  getCurrentLangText,
  getLocalTime,
  getNeedManageSites,
  getSiteOptions,
  getSiteText,
  sortSites,
} from '../../lib';
import { DownloadNewsEdit } from './components/download-news-edit';
import { messageError, messageSuccess } from '../../components/message';
import { DeployArea, NewsModule } from '../../model';
import { AutoAntTable } from '../../components/auto-ant-table';
import {
  CommonAddButton,
  CommonButton,
  CommonButtonGroup,
} from '../../components/button/main';
import { wrapRequestArea } from '../../lib/http/lib';
import { getAntLastModifyColumn } from './lib/columns';
import { CustomPopover } from '../../components/custom-popover';
import { getTableID } from './lib/id-helper';
import { IRootState } from '../../store/reducers';

export const AdminDownloadNews = () => {
  const [newsList, setNewsList] = useState<IDownloadNews[]>([]);
  const [newsListLoading, setNewsListLoading] = useState<boolean>(false);
  const [deleteNewsLoading, setDeleteNewsLoading] = useState<boolean>(false);
  const [showNewsModal, setShowNewsModal] = useState<boolean>(false);
  const [currentRecord, setCurrentRecord] = useState<IDownloadNews | null>(
    null
  );
  const [showDeleteNewsModal, setShowDeleteNewsModal] =
    useState<boolean>(false);
  const [deleteRecord, setDeleteRecord] = useState<IDownloadNews | null>(null);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  const menuDispatch = useDispatch<Dispatch<CommonAction>>();

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.DownLoadNews,
  });

  useEffect(() => {
    getNewsList();
  }, []);
  const getNewsList = async () => {
    setNewsListLoading(true);

    const allSiteRes: Record<string, IDownloadNews[]> = {};
    await Promise.all(
      getNeedManageSites().map(async (site) => {
        const siteRes = await wrapRequestArea(
          () =>
            getDownLoadNewsList({
              Module: String(NewsModule.DownloadCenter),
            }),
          site
        );

        allSiteRes[site] = siteRes.map((item) => {
          return { ...item, site };
        });
      })
    );

    const allSiteList = Object.values(allSiteRes).flat();
    const showedList: IDownloadNews[] = [];
    allSiteList.forEach((item) => {
      const currentSyncIds = showedList
        .map((item) => item.DataSyncID)
        .filter(Boolean);
      if (!item.DataSyncID || !currentSyncIds.includes(item.DataSyncID)) {
        showedList.push(item);
      }
    });
    const getCombinedRecords = (record: IDownloadNews) => {
      const matched = allSiteList.filter(
        (siteItem) =>
          record.DataSyncID && siteItem.DataSyncID === record.DataSyncID
      );
      return matched.length ? matched : undefined;
    };

    const siteNewsList: IDownloadNews[] = showedList
      .map((item) => ({
        ...item,
        allSiteRecord: getCombinedRecords(item),
      }))
      .sort((a, b) => (a.ModifyTime < b.ModifyTime ? 1 : -1));

    setNewsList(siteNewsList);
    setNewsListLoading(false);
  };

  const COLUMNS = [
    {
      title: getFormatMessage('id'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string, record: any) => {
        return getTableID(record);
      },
    },
    {
      title: getFormatMessage('标题名称'),
      dataIndex: 'Title',
      key: 'Title',
      render: (text: string) => {
        return getCurrentLangText(text);
      },
    },
    {
      title: getFormatMessage('description'),
      dataIndex: 'Description',
      key: 'Description',
      render: (text: string) => {
        return (
          <CustomPopover content={getCurrentLangText(text)}>
            <span>{getCurrentLangText(text)}</span>
          </CustomPopover>
        );
      },
    },
    {
      title: getFormatMessage('发布站点'),
      dataIndex: 'site',
      key: 'site',
      render: (text: DeployArea, record: IDownloadNews) => {
        return (
          sortSites(
            record.allSiteRecord?.map((item) => item.site || '').filter(Boolean)
          )
            ?.map((site) => getSiteText(site))
            .join('/') || getSiteText(record.site)
        );
      },
    },
    {
      title: getFormatMessage('create_time_local'),
      dataIndex: 'CreateTime',
      key: 'CreateTime',
      width: '200px',
      render: (text: Date) => {
        return getLocalTime(text, userInfo?.UserSettings?.Timezone);
      },
    },
    getAntLastModifyColumn({}, userInfo),
    {
      title: getFormatMessage('operate'),
      key: 'Operate',
      width: '150px',
      render: (_text: string, record: IDownloadNews) => {
        return (
          <CommonButtonGroup>
            <CommonButton type="link" onClick={() => handleEdit(record)}>
              {getFormatMessage('edit')}
            </CommonButton>
            <CommonButton
              type="link"
              className="ant-btn-dangerous"
              onClick={() => deleteNews(record)}
            >
              {getFormatMessage('delete')}
            </CommonButton>
          </CommonButtonGroup>
        );
      },
    },
  ];

  const deleteNews = (record: IDownloadNews) => {
    setShowDeleteNewsModal(true);
    setDeleteRecord(record);
  };

  const handleEdit = (record: IDownloadNews) => {
    setShowNewsModal(true);
    setCurrentRecord(record);
  };

  const onDeleteCancel = () => {
    setShowDeleteNewsModal(false);
    setDeleteRecord(null);
  };

  const onDeleteNews = async () => {
    if (deleteRecord) {
      setDeleteNewsLoading(true);

      const deletedRecords = deleteRecord.allSiteRecord
        ? Object.values(deleteRecord.allSiteRecord)
        : [deleteRecord];

      const res = await Promise.all(
        deletedRecords.map((record) =>
          wrapRequestArea(() => deleteDownLoadNews(record?.id), record.site)
        )
      );

      setDeleteNewsLoading(false);
      if (res.every((item) => item === 0)) {
        messageSuccess(getFormatMessage('del_success'));
        onDeleteCancel();
        getNewsList();
        return;
      }
      messageError(getFormatMessage('error_delete'));
    }
  };

  const renderDeleteNewsModal = () => {
    return (
      <Modal
        open={showDeleteNewsModal && !!deleteRecord}
        title={getFormatMessage('delete')}
        onCancel={onDeleteCancel}
        maskClosable={false}
        onOk={onDeleteNews}
        okText={getFormatMessage('delete')}
        confirmLoading={deleteNewsLoading}
      >
        <div>{`${getFormatMessage('del_news_sure_desc')} ${getCurrentLangText(
          deleteRecord?.Title || ''
        )}`}</div>
      </Modal>
    );
  };

  const renderNewsModal = () => {
    if (showNewsModal) {
      return (
        <DownloadNewsEdit
          onClose={() => setShowNewsModal(false)}
          onSave={getNewsList}
          currentRecord={currentRecord}
        />
      );
    }
    return null;
  };

  const clickCreate = () => {
    setShowNewsModal(true);
    setCurrentRecord(null);
  };

  return (
    <div>
      <AutoAntTable
        loading={newsListLoading}
        dataSource={newsList}
        columns={COLUMNS}
        configFilter={{
          filters: [
            {
              name: 'create',
              render() {
                return (
                  <CommonAddButton onClick={clickCreate}>
                    {getFormatMessage('创建通知')}
                  </CommonAddButton>
                );
              },
            },
            {
              label: '发布站点',
              name: 'site',
              type: 'select',
              options: getSiteOptions(),
              filter: (val, record) => {
                return (
                  !!record.allSiteRecord?.some((item) => item.site === val) ||
                  record.site === val
                );
              },
            },

            {
              name: 'Title',
              type: 'search',
              placeholder: '请输入标题搜索',
              place: 'right',
            },
          ],
        }}
      />
      {renderNewsModal()}
      {renderDeleteNewsModal()}
    </div>
  );
};
